import React, { useState } from 'react'
import { Button } from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import { Badge, StatusBadge } from '../components/ui/Badge'
import { Input, Textarea, Select } from '../components/ui/Input'
import { Alert } from '../components/ui/Alert'
import { Modal } from '../components/ui/Modal'
import { Tabs } from '../components/ui/Tabs'
import { ProgressBar } from '../components/ui/ProgressBar'
import { Avatar } from '../components/ui/Avatar'
import { Skeleton } from '../components/ui/Skeleton'
import { EmptyState } from '../components/ui/EmptyState'
import { SURVEY_GROUP_PALETTE, safeFill } from '../styles/colorContrast'

const StyleGuidePage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-heading">Sondely Design System</h1>
          <p className="mt-2 text-lg text-foreground-muted">Gentle Glow — Component Library</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-12">

        {/* Colors */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Colors</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Primary (Indigo)</h3>
              <div className="space-y-1">
                <div className="h-8 rounded bg-primary/10 px-3 flex items-center text-xs">10%</div>
                <div className="h-8 rounded bg-primary/20 px-3 flex items-center text-xs">20%</div>
                <div className="h-8 rounded bg-primary-light px-3 flex items-center text-xs text-white">Light</div>
                <div className="h-8 rounded bg-primary px-3 flex items-center text-xs text-white">Default</div>
                <div className="h-8 rounded bg-primary-dark px-3 flex items-center text-xs text-white">Dark</div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Secondary (Mint)</h3>
              <div className="space-y-1">
                <div className="h-8 rounded bg-secondary/10 px-3 flex items-center text-xs">10%</div>
                <div className="h-8 rounded bg-secondary-light px-3 flex items-center text-xs">Light</div>
                <div className="h-8 rounded bg-secondary px-3 flex items-center text-xs text-white">Default</div>
                <div className="h-8 rounded bg-secondary-dark px-3 flex items-center text-xs text-white">Dark</div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Accent (Amber)</h3>
              <div className="space-y-1">
                <div className="h-8 rounded bg-accent/10 px-3 flex items-center text-xs">10%</div>
                <div className="h-8 rounded bg-accent px-3 flex items-center text-xs text-white">Default</div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Semantic</h3>
              <div className="space-y-1">
                <div className="h-8 rounded bg-success px-3 flex items-center text-xs text-white">Success</div>
                <div className="h-8 rounded bg-warning px-3 flex items-center text-xs">Warning</div>
                <div className="h-8 rounded bg-error px-3 flex items-center text-xs text-white">Error</div>
                <div className="h-8 rounded bg-info px-3 flex items-center text-xs text-white">Info</div>
              </div>
            </div>
          </div>
        </section>

        {/* Survey group colors */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Survey group colors</h2>
          <Card padding="lg">
            <p className="text-sm text-foreground-muted mb-6 max-w-3xl">
              These are per-survey content colors chosen by survey authors, not fixed brand tokens.
              An author picks a color per group, and every one is rendered through a contrast-safe
              primitive (<code className="text-xs bg-background px-1 py-0.5 rounded">safeFill</code>) so
              the label stays readable no matter which color is chosen. The chips below show each palette
              swatch as a selected rating, using its real contrast-safe background and text color.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {SURVEY_GROUP_PALETTE.map((c) => {
                const f = safeFill(c.value)
                return (
                  <div key={c.value} className="space-y-2">
                    <div
                      className="h-16 rounded-lg flex items-center justify-between px-4 font-semibold"
                      style={{ backgroundColor: f.background, color: f.text }}
                    >
                      <span className="text-sm">{c.label}</span>
                      <span className="text-2xl font-bold">3</span>
                    </div>
                    <p className="text-xs text-foreground-muted">{c.value}</p>
                  </div>
                )
              })}
            </div>
          </Card>
        </section>

        {/* Typography */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Typography</h2>
          <Card padding="lg">
            <div className="space-y-4">
              <div>
                <span className="text-xs text-foreground-muted uppercase tracking-wide">Heading Font: DM Sans</span>
                <h1 className="text-5xl font-bold text-foreground">Heading 1 (48px)</h1>
              </div>
              <h2 className="text-4xl font-bold text-foreground">Heading 2 (36px)</h2>
              <h3 className="text-3xl font-bold text-foreground">Heading 3 (30px)</h3>
              <h4 className="text-2xl font-bold text-foreground">Heading 4 (24px)</h4>
              <hr className="border-border" />
              <div>
                <span className="text-xs text-foreground-muted uppercase tracking-wide">Body Font: Inter</span>
                <p className="text-lg text-foreground">Body Large (18px)</p>
              </div>
              <p className="text-base text-foreground">Body Base (16px) — The default body text size.</p>
              <p className="text-sm text-foreground-muted">Body Small (14px) — Labels and secondary information.</p>
              <p className="text-xs text-foreground-muted">Body Extra Small (12px) — Timestamps and metadata.</p>
            </div>
          </Card>
        </section>

        {/* Buttons */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Buttons</h2>
          <Card padding="lg">
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground-muted mb-3">Variants</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="accent">Accent</Button>
                  <Button variant="danger">Danger</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-muted mb-3">Sizes</h3>
                <div className="flex items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button size="md">Medium</Button>
                  <Button size="lg">Large</Button>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-muted mb-3">States</h3>
                <div className="flex flex-wrap gap-3">
                  <Button loading>Loading</Button>
                  <Button disabled>Disabled</Button>
                  <Button variant="accent" fullWidth>Full Width</Button>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* Cards */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Cards</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Card>
              <CardHeader title="Default Card" subtitle="With border, no shadow" />
              <p className="text-sm text-foreground-muted">Standard card for content sections.</p>
            </Card>
            <Card elevated>
              <CardHeader title="Elevated Card" subtitle="With shadow, no border" />
              <p className="text-sm text-foreground-muted">For floating or prominent content.</p>
            </Card>
            <Card>
              <CardHeader title="Card with Action" action={<Button size="sm">Action</Button>} />
              <p className="text-sm text-foreground-muted">Header supports an action slot.</p>
            </Card>
            <Card padding="lg">
              <CardHeader title="Large Padding" />
              <p className="text-sm text-foreground-muted">More breathing room for important content.</p>
            </Card>
          </div>
        </section>

        {/* Badges */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Badges</h2>
          <Card padding="lg">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground-muted mb-3">Semantic Variants</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="primary">Primary</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="success">Success</Badge>
                  <Badge variant="warning">Warning</Badge>
                  <Badge variant="error">Error</Badge>
                  <Badge variant="info">Info</Badge>
                  <Badge variant="neutral">Neutral</Badge>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-muted mb-3">Status Badges</h3>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge status="PUBLISHED" />
                  <StatusBadge status="DRAFT" />
                  <StatusBadge status="CLOSED" />
                  <StatusBadge status="PENDING" />
                  <StatusBadge status="OWNER" />
                  <StatusBadge status="ADMIN" />
                  <StatusBadge status="RESPONDENT" />
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* Tabs */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Tabs</h2>
          <Card padding="lg">
            <Tabs
              tabs={[
                { key: 'overview', label: 'Overview' },
                { key: 'analytics', label: 'Analytics' },
                { key: 'settings', label: 'Settings' },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
            <div className="pt-4 text-sm text-foreground-muted">
              Active tab: <span className="font-medium text-foreground">{activeTab}</span>
            </div>
          </Card>
        </section>

        {/* Progress Bar */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Progress Bars</h2>
          <Card padding="lg">
            <div className="space-y-6">
              <ProgressBar value={75} label="Survey Completion" showPercentage />
              <ProgressBar value={45} variant="success" size="sm" />
              <ProgressBar value={90} variant="warning" size="lg" label="Storage Used" showPercentage />
              <ProgressBar value={20} variant="error" />
            </div>
          </Card>
        </section>

        {/* Avatars */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Avatars</h2>
          <Card padding="lg">
            <div className="flex items-center gap-4">
              <Avatar name="John Doe" size="sm" />
              <Avatar name="Jane Smith" size="md" />
              <Avatar name="Alex Johnson" size="lg" />
              <Avatar size="md" />
            </div>
          </Card>
        </section>

        {/* Skeleton Loading */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Skeleton Loading</h2>
          <Card padding="lg">
            <div className="space-y-3">
              <Skeleton width="60%" />
              <Skeleton width="100%" />
              <Skeleton width="80%" />
              <div className="flex items-center gap-3 pt-2">
                <Skeleton variant="circular" width={40} height={40} />
                <div className="flex-1 space-y-2">
                  <Skeleton width="40%" />
                  <Skeleton width="70%" />
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* Empty State */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Empty State</h2>
          <Card padding="none">
            <EmptyState
              title="No Surveys Found"
              description="Get started by creating your first survey to measure impact."
              actionLabel="Create Survey"
              onAction={() => {}}
            />
          </Card>
        </section>

        {/* Modal */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Modal</h2>
          <Card padding="lg">
            <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Sample Modal">
              <p className="text-sm text-foreground-muted mb-4">
                This is a modal dialog. It overlays the page and focuses attention.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button onClick={() => setModalOpen(false)}>Confirm</Button>
              </div>
            </Modal>
          </Card>
        </section>

        {/* Form Inputs */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Form Inputs</h2>
          <Card padding="lg">
            <div className="space-y-4 max-w-md">
              <Input label="Email" id="demo-email" type="email" placeholder="you@example.com" required />
              <Input label="With Error" id="demo-error" placeholder="Something wrong" error="This field is required" />
              <Input label="With Helper" id="demo-helper" placeholder="Enter name" helperText="First and last name" />
              <Input label="Disabled" id="demo-disabled" placeholder="Cannot edit" disabled />
              <Textarea label="Description" id="demo-textarea" placeholder="Tell us more..." rows={3} />
              <Select
                label="Organization"
                id="demo-select"
                required
                placeholder="Select one"
                options={[
                  { value: 'org1', label: 'CatalyzeUp Nonprofit' },
                  { value: 'org2', label: 'Community Health Network' },
                ]}
              />
            </div>
          </Card>
        </section>

        {/* Alerts */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Alerts</h2>
          <div className="space-y-3">
            <Alert variant="success">Survey published successfully.</Alert>
            <Alert variant="warning">This survey is still in draft mode.</Alert>
            <Alert variant="error">Failed to save. Please try again.</Alert>
            <Alert variant="info">Tip: You can invite members from the organization detail page.</Alert>
          </div>
        </section>

        {/* Shadows */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Shadows & Radius</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div className="bg-surface rounded-sm shadow-sm p-6 text-center text-sm text-foreground-muted">shadow-sm<br />rounded-sm</div>
            <div className="bg-surface rounded shadow p-6 text-center text-sm text-foreground-muted">shadow<br />rounded</div>
            <div className="bg-surface rounded-lg shadow-md p-6 text-center text-sm text-foreground-muted">shadow-md<br />rounded-lg</div>
            <div className="bg-surface rounded-xl shadow-lg p-6 text-center text-sm text-foreground-muted">shadow-lg<br />rounded-xl</div>
          </div>
        </section>

        {/* Spacing */}
        <section>
          <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">Spacing Scale (8px base)</h2>
          <Card padding="lg">
            <div className="space-y-2">
              {[
                { name: '0.5', px: '4px' },
                { name: '1', px: '8px' },
                { name: '1.5', px: '12px' },
                { name: '2', px: '16px' },
                { name: '3', px: '24px' },
                { name: '4', px: '32px' },
                { name: '6', px: '48px' },
                { name: '8', px: '64px' },
              ].map(s => (
                <div key={s.name} className="flex items-center gap-4">
                  <span className="text-xs text-foreground-muted w-16">{s.name} ({s.px})</span>
                  <div className="bg-primary-light rounded" style={{ width: s.px, height: '16px' }} />
                </div>
              ))}
            </div>
          </Card>
        </section>

        <div className="text-center text-sm text-foreground-muted py-8">
          Sondely Design System — Gentle Glow
        </div>
      </div>
    </div>
  )
}

export default StyleGuidePage
